import { Box, Skeleton } from '@mui/material';

/**
 * SkeletonLoader component - Content placeholders for loading states
 *
 * Provides consistent skeleton loading patterns for different content types:
 * - 'list': Vertical list of skeleton items
 * - 'table': Table-like skeleton rows
 * - 'card': Card-like skeleton blocks
 * - 'text': Text line skeletons
 *
 * @example
 * ```tsx
 * // List skeleton (recipes, users, etc.)
 * if (isLoading) return <SkeletonLoader variant="list" count={5} />;
 *
 * // Table skeleton
 * if (isLoading) return <SkeletonLoader variant="table" count={10} height={52} />;
 *
 * // Card grid skeleton
 * if (isLoading) return <SkeletonLoader variant="card" count={3} />;
 *
 * // Text lines
 * if (isLoading) return <SkeletonLoader variant="text" count={3} />;
 * ```
 */

export interface SkeletonLoaderProps {
  /**
   * Type of skeleton layout to render
   */
  variant: 'list' | 'table' | 'card' | 'text';

  /**
   * Number of skeleton items to render
   * @default 3
   */
  count?: number;

  /**
   * Height of each skeleton item
   * @default varies by variant (56px for list, 52px for table, 200px for card, auto for text)
   */
  height?: number | string;

  /**
   * Animation type
   * @default 'wave'
   */
  animation?: 'pulse' | 'wave' | false;
}

export const SkeletonLoader = ({
  variant,
  count = 3,
  height,
  animation = 'wave',
}: SkeletonLoaderProps) => {
  // Determine default height based on variant
  const getDefaultHeight = () => {
    switch (variant) {
      case 'list':
        return 56;
      case 'table':
        return 52;
      case 'card':
        return 200;
      case 'text':
        return undefined; // Auto height for text
      default:
        return 56;
    }
  };

  const itemHeight = height ?? getDefaultHeight();

  // Render different layouts based on variant
  const renderSkeletons = () => {
    const items = Array.from({ length: count }, (_, index) => index);

    switch (variant) {
      case 'list':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((i) => (
              <Skeleton key={i} variant="rounded" height={itemHeight} animation={animation} />
            ))}
          </Box>
        );

      case 'table':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {items.map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={itemHeight}
                animation={animation}
                sx={{ mb: 0.5 }}
              />
            ))}
          </Box>
        );

      case 'card':
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 2,
            }}
          >
            {items.map((i) => (
              <Skeleton key={i} variant="rounded" height={itemHeight} animation={animation} />
            ))}
          </Box>
        );

      case 'text':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((i) => (
              <Skeleton
                key={i}
                variant="text"
                height={itemHeight}
                animation={animation}
                sx={{ fontSize: '1rem' }}
              />
            ))}
          </Box>
        );

      default:
        return null;
    }
  };

  return <>{renderSkeletons()}</>;
};
